import React from "react";
import { Link } from "react-router-dom";
import { BarChart3, ExternalLink } from "lucide-react";
import { formatMoney, formatBudgetRange } from "../../helpers/copilotInsights";
import "./CopilotInsightsCard.scss";

const StatTile = ({ label, value }) => (
  <div className="copilot-insights__stat">
    <p className="copilot-insights__stat-label">{label}</p>
    <p className="copilot-insights__stat-value">{value}</p>
  </div>
);

const BreakdownRows = ({ rows }) => (
  <div className="copilot-insights__breakdown">
    {rows.map((row) => (
      <div className="copilot-insights__breakdown-row" key={row.label}>
        <span className="copilot-insights__breakdown-label">{row.label}</span>
        <span className="copilot-insights__breakdown-bar">
          <span
            className="copilot-insights__breakdown-fill"
            style={{ width: `${row.percent}%` }}
          />
        </span>
        <span className="copilot-insights__breakdown-percent">{row.percent}%</span>
      </div>
    ))}
  </div>
);

const MatchRow = ({ match, openInPlace, disableLinks }) => {
  const meta = [match.budget, match.financeType, match.timeline]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="copilot-insights__match">
      <span className="copilot-insights__match-score">{match.score ?? "—"}</span>
      <div className="copilot-insights__match-body">
        <p className="copilot-insights__match-id">Match {match.id}</p>
        {(meta || match.story) && (
          <p className="copilot-insights__match-meta">
            {meta}
            {meta && match.story ? " · " : ""}
            {match.story}
          </p>
        )}
      </div>
      {match.href &&
        (disableLinks && openInPlace ? (
          <span className="copilot-insights__match-link copilot-insights__match-link--disabled" aria-disabled="true">
            View
          </span>
        ) : (
          <Link
            to={match.href}
            target={openInPlace ? undefined : "_blank"}
            rel={openInPlace ? undefined : "noopener noreferrer"}
            className="copilot-insights__match-link"
          >
            View
            <ExternalLink size={12} aria-hidden="true" />
          </Link>
        ))}
    </div>
  );
};

const CopilotInsightsCard = ({ insights, onChipSend, openInPlace, disableLinks }) => {
  if (!insights) return null;

  const { stats, budget, financeMix, timeline, topFeatures, topMatches } = insights;
  const askPrice = formatMoney(insights.askPrice);
  const remainingMatches = stats.total - topMatches.length;

  return (
    <section className="copilot-insights" aria-label="Demand insights">
      <header className="copilot-insights__header">
        <BarChart3 size={17} className="copilot-insights__header-icon" aria-hidden="true" />
        <h4 className="copilot-insights__title">Demand insights</h4>
        <span className="copilot-insights__subtitle">
          {insights.title}
          {askPrice ? ` · ${askPrice}` : ""}
        </span>
      </header>

      <div className="copilot-insights__stats">
        <StatTile label="Matched buyers" value={stats.total} />
        <StatTile label="Top score" value={stats.top ?? "—"} />
        <StatTile label="Avg score" value={stats.avg ?? "—"} />
        <StatTile label="New (30d)" value={stats.postedLast30Count} />
      </div>

      {budget && (budget.sweetSpotMin || budget.sweetSpotMax) && (
        <div className="copilot-insights__section">
          <p className="copilot-insights__section-label">
            Budget
            {budget.sampleSize
              ? ` · ${budget.sampleSize} of ${stats.total} buyers shared budget`
              : ""}
          </p>
          <p className="copilot-insights__budget-range">
            {formatBudgetRange(budget.sweetSpotMin, budget.sweetSpotMax)}
            {budget.isPartialSample ? " (partial sample)" : ""}
          </p>
        </div>
      )}

      {(financeMix.length > 0 || timeline.length > 0) && (
        <div className="copilot-insights__columns">
          {financeMix.length > 0 && (
            <div className="copilot-insights__section">
              <p className="copilot-insights__section-label">Financing</p>
              <BreakdownRows rows={financeMix} />
            </div>
          )}
          {timeline.length > 0 && (
            <div className="copilot-insights__section">
              <p className="copilot-insights__section-label">Timeline</p>
              <BreakdownRows rows={timeline} />
            </div>
          )}
        </div>
      )}

      {topFeatures.length > 0 && (
        <div className="copilot-insights__section">
          <p className="copilot-insights__section-label">Most requested features</p>
          <BreakdownRows rows={topFeatures} />
        </div>
      )}

      {topMatches.length > 0 && (
        <div className="copilot-insights__section">
          <p className="copilot-insights__section-label">Top matches</p>
          <div className="copilot-insights__matches">
            {topMatches.map((match) => (
              <MatchRow
                key={match.id}
                match={match}
                openInPlace={openInPlace}
                disableLinks={disableLinks}
              />
            ))}
          </div>
          {remainingMatches > 0 && onChipSend && (
            <button
              type="button"
              className="copilot-insights__view-all"
              onClick={() => onChipSend(`Show me all ${stats.total} matches for this listing`)}
            >
              View all {stats.total} matches
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default CopilotInsightsCard;
